import type { GeneratedStatus } from "@keydials/api-client";

/** One action point, as far as "could this person set a status here?" is concerned. */
export interface AudienceSegment {
  key: string;
  /**
   * True when this action point is scored by its ITEMS. Per action point, not per dial: an itemised dial's
   * action point with no items yet keeps its own status pill, and a brand-new dial — exactly the first-run
   * case — may be full of them.
   */
  itemActive: boolean;
  /** The action point's own palette, used when it isn't item-scored. */
  palette: readonly GeneratedStatus[];
}

export interface AudienceSection {
  key: string;
  segments: readonly AudienceSegment[];
}

/** The slice of the dial VM this file needs — narrow on purpose, so the tests can build one by hand. */
export interface StatusAudienceVm {
  sections: readonly AudienceSection[];
  /** The scoring axes of an itemised dial; each carries the palette its items are set from. */
  dimensions: readonly { palette: { statuses: GeneratedStatus[] } }[];
}

/**
 * Whether there is anywhere on this dial that this person could actually set a status (KEY-535).
 *
 * The first-run tour uses this to decide whether to teach the gesture at all. It matters because
 * `StatusPicker` always renders — statuses the caller may not set are shown greyed out rather than hidden —
 * so the tour's anchor resolves for a Reader just as it does for an Editor, and the tour would cheerfully
 * spotlight a control and describe an action they cannot perform. A section-scoped grant that happens to
 * carry no settable status anywhere is the same case, less obviously.
 *
 * Asked per action point rather than per dial, against whichever palette actually governs it: the dimension
 * palettes where the action point is item-scored, its own palette where it isn't. `canSet` is the caller's —
 * the same one the panels use, with role simulation (KEY-498) already folded in.
 */
export function canSetAnyStatus(
  vm: StatusAudienceVm,
  canSet: (status: GeneratedStatus, sectionKey: string, segmentKey: string) => boolean,
): boolean {
  const itemStatuses = vm.dimensions.flatMap((d) => d.palette.statuses);
  return vm.sections.some((section) =>
    section.segments.some((segment) => {
      const statuses = segment.itemActive ? itemStatuses : segment.palette;
      return statuses.some((status) => canSet(status, section.key, segment.key));
    }),
  );
}
